import { useCallback, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { useLanguage } from '../../context/LanguageContext';
import { userService } from '../../services/api';
import { Loader2, ShieldCheck, X, CheckCircle2 } from 'lucide-react';

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

const CheckoutModal = ({ paymentData, onClose }) => {
    const { language } = useLanguage();
    const [error, setError] = useState(null);
    // Commande 100% gratuite : le backend crée la commande directement (pas de Stripe)
    const [freeSuccess, setFreeSuccess] = useState(false);

    const fetchClientSecret = useCallback(async () => {
        try {
            const data = await userService.createPaymentSession(paymentData);
            if (data.status === 'free') {
                setFreeSuccess(true);
                throw new Error('FREE_ORDER'); // pas de clientSecret — on affiche l'écran succès
            }
            return data.clientSecret;
        } catch (err) {
            if (err.message !== 'FREE_ORDER') {
                const msg = err.response?.data?.error || err.message || 'Payment error';
                setError(msg);
            }
            throw err;
        }
    }, [paymentData]);

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-3xl w-full max-w-lg max-h-[90vh] flex flex-col shadow-2xl border border-gray-200 dark:border-white/10 animate-in zoom-in-95 duration-200 overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-100 dark:bg-green-500/10 rounded-xl">
                            <ShieldCheck className="w-5 h-5 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                                {language === 'fr' ? 'Paiement sécurisé' : 'Secure payment'}
                            </h2>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                                {language === 'fr' ? 'Propulsé par Stripe' : 'Powered by Stripe'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="w-8 h-8 rounded-full flex items-center justify-center text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Checkout form */}
                <div className="flex-1 overflow-y-auto">
                    {freeSuccess ? (
                        <div className="p-8 text-center">
                            <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center">
                                <CheckCircle2 className="w-8 h-8 text-green-500" />
                            </div>
                            <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                                {language === 'fr' ? 'Abonnement activé !' : 'Subscription activated!'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                                {language === 'fr'
                                    ? 'Ton code de 100% a été appliqué — aucun paiement requis.'
                                    : 'Your 100% code was applied — no payment required.'}
                            </p>
                            <button
                                onClick={() => { window.location.href = '/dashboard'; }}
                                className="px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold rounded-xl text-sm hover:opacity-90 transition shadow-lg shadow-green-500/20"
                            >
                                {language === 'fr' ? 'Accéder à mon tableau de bord' : 'Go to my dashboard'}
                            </button>
                        </div>
                    ) : error ? (
                        <div className="p-6 text-center">
                            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-500/10 flex items-center justify-center">
                                <X className="w-6 h-6 text-red-500" />
                            </div>
                            <p className="text-red-600 dark:text-red-400 font-bold mb-2">
                                {language === 'fr' ? 'Erreur' : 'Error'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{error}</p>
                            <button
                                onClick={onClose}
                                className="px-6 py-2 bg-gray-100 dark:bg-white/10 text-gray-700 dark:text-gray-300 font-bold rounded-xl text-sm hover:bg-gray-200 dark:hover:bg-white/15 transition"
                            >
                                {language === 'fr' ? 'Fermer' : 'Close'}
                            </button>
                        </div>
                    ) : (
                        <div id="checkout" className="p-4">
                            <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                                <EmbeddedCheckout />
                            </EmbeddedCheckoutProvider>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CheckoutModal;
